import * as XLSX from 'xlsx';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '@festpix/services';
import { getAllWallets, getTransactions } from './adminWalletService';
import { getAllUsers } from './adminUserService';
import { getAllBooths, BoothWithBalance } from '@festpix/services';

function downloadWorkbook(wb: XLSX.WorkBook, filename: string) {
  XLSX.writeFile(wb, filename);
}

function formatTimestamp(timestamp: any): string {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return date.toLocaleString('pt-BR');
}

function formatCurrency(value: number): string {
  return `R$ ${value.toFixed(2)}`;
}

function getTransactionTypeLabel(type: string, paymentMethod?: string): string {
  const labels: Record<string, string> = {
    purchase: 'Compra',
    recharge: 'Recarga',
    manual_recharge: 'Recarga Manual',
    transfer: 'Transferência',
    refund: 'Estorno',
  };

  let label = labels[type] || type;

  if (type === 'recharge' && paymentMethod) {
    const methodLabels: Record<string, string> = {
      pix: 'PIX',
      credit_card: 'Cartão de Crédito',
      debit_card: 'Cartão de Débito',
      checkout_pro: 'Mercado Pago',
    };
    const methodLabel = methodLabels[paymentMethod] || paymentMethod;
    label = `${label} (${methodLabel})`;
  }

  return label;
}

function getRoleLabel(role: string): string {
  const labels: Record<string, string> = {
    admin: 'Administrador',
    vendor: 'Vendedor',
    attendee: 'Participante',
  };
  return labels[role] || role;
}

interface WalletInfo {
  name: string;
  email: string;
  isChildWallet: boolean;
  childName?: string;
}

async function buildWalletLookupMap(): Promise<Map<string, WalletInfo>> {
  const result = await getAllWallets();
  const walletMap = new Map<string, WalletInfo>();

  for (const wallet of result.wallets) {
    walletMap.set(wallet.id, {
      name: wallet.name || '',
      email: wallet.email || '',
      isChildWallet: wallet.isChildWallet || false,
      childName: wallet.childName,
    });
  }

  // Also get booth wallets
  const boothsSnapshot = await getDocs(collection(db, 'booths'));
  const walletsSnapshot = await getDocs(collection(db, 'wallets'));

  for (const walletDoc of walletsSnapshot.docs) {
    const walletData = walletDoc.data();
    if (walletData.type === 'booth' || walletData.ownerId === 'booth') {
      const booth = boothsSnapshot.docs.find(
        (b) => b.data().mainWalletId === walletDoc.id
      );
      walletMap.set(walletDoc.id, {
        name: booth?.data().name || walletData.boothName || 'Estande',
        email: '',
        isChildWallet: false,
      });
    }
  }

  return walletMap;
}

async function buildUserLookupMap(): Promise<Map<string, { name: string; email: string }>> {
  const users = await getAllUsers();
  const userMap = new Map<string, { name: string; email: string }>();

  for (const user of users) {
    userMap.set(user.id, {
      name: user.name || '',
      email: user.email || '',
    });
  }

  return userMap;
}

async function buildBoothLookupMap(): Promise<Map<string, string>> {
  const booths = await getAllBooths();
  return new Map(booths.map((b: BoothWithBalance): [string, string] => [b.id, b.name]));
}

function getWalletDisplayName(walletId: string, walletMap: Map<string, WalletInfo>): string {
  if (!walletId) return '';
  const wallet = walletMap.get(walletId);
  if (!wallet) return walletId;

  if (wallet.isChildWallet && wallet.childName) {
    return wallet.childName;
  }
  return wallet.name || wallet.email || walletId;
}

export async function exportTransactions(): Promise<void> {
  const [transactionsResult, walletMap] = await Promise.all([
    getTransactions(),
    buildWalletLookupMap(),
  ]);

  const data = transactionsResult.transactions.map((tx: any) => {
    const fromWallet = walletMap.get(tx.fromWalletId);
    const toWallet = walletMap.get(tx.toWalletId);

    return {
      Data: formatTimestamp(tx.timestamp),
      Tipo: getTransactionTypeLabel(tx.type, tx.paymentMethod),
      Valor: formatCurrency(tx.amount),
      'De (Nome)': getWalletDisplayName(tx.fromWalletId, walletMap),
      'De (Email)': fromWallet?.email || '',
      'Para (Nome)': tx.boothName || getWalletDisplayName(tx.toWalletId, walletMap),
      'Para (Email)': toWallet?.email || '',
      Estande: tx.boothName || '',
      'Realizado Por': tx.performedBy?.name || tx.performedBy?.email || '',
      'Método Pagamento': tx.paymentMethod ? getPaymentMethodLabel(tx.paymentMethod) : '',
      Status: tx.status ? getStatusLabel(tx.status) : '',
      Notas: tx.notes || '',
    };
  });

  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Transações');

  const filename = `transacoes_${new Date().toISOString().split('T')[0]}.xlsx`;
  downloadWorkbook(wb, filename);
}

function getPaymentMethodLabel(method: string): string {
  const labels: Record<string, string> = {
    pix: 'PIX',
    credit_card: 'Cartão de Crédito',
    debit_card: 'Cartão de Débito',
    checkout_pro: 'Mercado Pago',
  };
  return labels[method] || method;
}

function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    pending: 'Pendente',
    approved: 'Aprovado',
    failed: 'Falhou',
    cancelled: 'Cancelado',
  };
  return labels[status] || status;
}

export async function exportWallets(): Promise<void> {
  const result = await getAllWallets();

  const data = result.wallets.map((wallet: any) => ({
    Nome: wallet.name,
    Email: wallet.email || '',
    Saldo: formatCurrency(wallet.balance),
    'Tipo Carteira': wallet.isChildWallet ? 'Carteira Filho' : 'Carteira Principal',
    'Nome Filho': wallet.childName || '',
    'Carteira Pai': wallet.parentWalletName || '',
  }));

  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Carteiras');

  const filename = `carteiras_${new Date().toISOString().split('T')[0]}.xlsx`;
  downloadWorkbook(wb, filename);
}

export async function exportUsers(): Promise<void> {
  const [users, walletMap] = await Promise.all([
    getAllUsers(),
    buildWalletLookupMap(),
  ]);

  const data = users.map((user) => {
    const wallet = user.walletId ? walletMap.get(user.walletId) : null;
    return {
      Nome: user.name,
      Email: user.email,
      Perfil: getRoleLabel(user.role),
      'Saldo Carteira': wallet ? 'Ver relatório Carteiras' : 'Sem carteira',
    };
  });

  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Usuários');

  const filename = `usuarios_${new Date().toISOString().split('T')[0]}.xlsx`;
  downloadWorkbook(wb, filename);
}

export async function exportBooths(): Promise<void> {
  const [booths, userMap] = await Promise.all([
    getAllBooths(),
    buildUserLookupMap(),
  ]);

  const data = booths.map((booth: BoothWithBalance) => {
    const vendorNames = booth.vendorIds
      ?.map((id: string) => {
        const user = userMap.get(id);
        return user?.name || user?.email || id;
      })
      .join(', ') || '';

    return {
      Nome: booth.name,
      'Saldo Carteira': formatCurrency(booth.walletBalance || 0),
      'Qtd Vendedores': booth.vendorIds?.length || 0,
      Vendedores: vendorNames,
    };
  });

  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Estandes');

  const filename = `estandes_${new Date().toISOString().split('T')[0]}.xlsx`;
  downloadWorkbook(wb, filename);
}

export async function exportSKUs(): Promise<void> {
  const boothNames = await buildBoothLookupMap();
  const skusSnapshot = await getDocs(query(collection(db, 'skus'), orderBy('name', 'asc')));

  const data = skusSnapshot.docs.map((doc) => {
    const sku = doc.data();
    return {
      Nome: sku.name,
      'Nome Curto': sku.shortName,
      Preço: formatCurrency(sku.price),
      Quantidade: sku.quantity,
      'Controle Estoque': sku.controlInventory ? 'Sim' : 'Não',
      Estande: boothNames.get(sku.boothId) || 'Desconhecido',
    };
  });

  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'SKUs');

  const filename = `skus_${new Date().toISOString().split('T')[0]}.xlsx`;
  downloadWorkbook(wb, filename);
}

export async function exportSKUSalesCounters(): Promise<void> {
  const [salesSnapshot, skusSnapshot, boothsSnapshot] = await Promise.all([
    getDocs(collection(db, 'sku_sales_counters')),
    getDocs(collection(db, 'skus')),
    getDocs(collection(db, 'booths')),
  ]);

  const boothNames = new Map<string, string>();
  boothsSnapshot.docs.forEach((doc) => {
    boothNames.set(doc.id, doc.data().name);
  });

  const skuInfo = new Map<string, { name: string; shortName: string; boothId: string }>();
  skusSnapshot.docs.forEach((doc) => {
    const data = doc.data();
    skuInfo.set(doc.id, {
      name: data.name,
      shortName: data.shortName,
      boothId: data.boothId,
    });
  });

  const data = salesSnapshot.docs.map((doc) => {
    const sale = doc.data();
    const skuId = sale.skuId || doc.id;
    const sku = skuInfo.get(skuId);

    return {
      'SKU Nome': sku?.name || 'Desconhecido',
      'SKU Nome Curto': sku?.shortName || '',
      Estande: sku?.boothId ? boothNames.get(sku.boothId) || 'Desconhecido' : '',
      'Quantidade Vendida': sale.salesCount || 0,
    };
  });

  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Contadores de Vendas');

  const filename = `contadores_vendas_${new Date().toISOString().split('T')[0]}.xlsx`;
  downloadWorkbook(wb, filename);
}
